/**
 * RingBuffer - A fixed-size circular buffer implementation with deduplication
 * Used for managing real-time transcripts and suggestions with memory efficiency
 */

export interface BufferStats {
  size: number;
  capacity: number;
  droppedCount: number;
}

export class RingBuffer<T> {
  private buffer: T[] = [];
  private idSet = new Set<string>();
  private droppedCount = 0;
  
  constructor(
    private capacity: number,
    private getKey: (item: T) => string,
    private onDrop?: (item: T) => void
  ) {
    if (capacity < 1) throw new Error('Capacity must be positive');
  }

  /**
   * Add or update an item in the buffer
   * @returns true if item was added/updated, false if dropped
   */
  upsert(item: T): boolean {
    const key = this.getKey(item);
    
    // Update existing item
    const existingIndex = this.buffer.findIndex(x => this.getKey(x) === key);
    if (existingIndex >= 0) {
      this.buffer[existingIndex] = item;
      return true;
    }
    
    // Add new item
    if (this.buffer.length >= this.capacity) {
      const dropped = this.buffer.shift();
      if (dropped) {
        const droppedKey = this.getKey(dropped);
        this.idSet.delete(droppedKey);
        this.droppedCount++;
        this.onDrop?.(dropped);
      }
    }
    
    this.buffer.push(item);
    this.idSet.add(key);
    return true;
  }

  /**
   * Add multiple items efficiently
   */
  upsertMany(items: T[]): number {
    let addedCount = 0;
    for (const item of items) {
      if (this.upsert(item)) addedCount++;
    }
    return addedCount;
  }

  /**
   * Check if an item exists by its key
   */
  has(key: string): boolean {
    return this.idSet.has(key);
  }

  /**
   * Get current buffer contents
   */
  getItems(): T[] {
    return [...this.buffer];
  }

  /**
   * Get buffer statistics
   */
  getStats(): BufferStats {
    return {
      size: this.buffer.length,
      capacity: this.capacity,
      droppedCount: this.droppedCount
    };
  }

  /**
   * Clear the buffer
   */
  clear(): void {
    this.buffer = [];
    this.idSet.clear();
    this.droppedCount = 0;
  }
}

/**
 * Specialized RingBuffer for managing transcript chunks
 */
export interface TranscriptChunk {
  id: string;
  speaker: 'agent' | 'customer';
  text: string;
  timestamp: string;
  final: boolean;
  confidence?: number;
  callSid?: string;
}

export class TranscriptBuffer extends RingBuffer<TranscriptChunk> {
  constructor(capacity: number = 400) {
    super(capacity, chunk => chunk.id);
  }

  /**
   * Get total character count including dropped chunks
   */
  getTotalCharCount(): number {
    return this.getItems().reduce((sum, chunk) => sum + chunk.text.length, 0);
  }

  /**
   * Get chunks for a specific speaker
   */
  getSpeakerChunks(speaker: 'agent' | 'customer'): TranscriptChunk[] {
    return this.getItems().filter(chunk => chunk.speaker === speaker);
  }
}

/**
 * Specialized RingBuffer for managing coaching suggestions
 */
export interface CoachingSuggestion {
  id: string;
  type: 'script' | 'objection' | 'upsell' | 'compliance' | 'info';
  severity: 'critical' | 'warning' | 'suggestion' | 'info';
  title: string;
  message: string;
  suggestedScript?: string;
  timestamp: number;
  source?: 'claude' | 'openai' | 'heuristic';
  responseTime?: number;
}

export class SuggestionBuffer extends RingBuffer<CoachingSuggestion> {
  constructor(capacity: number = 100) {
    super(capacity, suggestion => suggestion.id);
  }

  /**
   * Get suggestions filtered by severity
   */
  getBySeverity(severity: CoachingSuggestion['severity']): CoachingSuggestion[] {
    return this.getItems().filter(s => s.severity === severity);
  }

  /**
   * Get suggestions filtered by source
   */
  getBySource(source: NonNullable<CoachingSuggestion['source']>): CoachingSuggestion[] {
    return this.getItems().filter(s => s.source === source);
  }
}